import * as React from "react"
import { motion } from "motion/react"
import { Trophy, ArrowUpDown } from "lucide-react"
import { useAuth, type AdminAgentPerformanceEntry } from "@/contexts/AuthContext"
import { AdminCard } from "./AdminCard"

const PODIUM_COLORS = ["#F59E0B", "#9CA3AF", "#CD7F32"]

type SortKey = "dailyDeals" | "totalDeals" | "monthlyDeals" | "weeklyDeals" | "points"

export function AdminAgents() {
  const { getAdminAgentPerformance } = useAuth()
  const [agents, setAgents] = React.useState<AdminAgentPerformanceEntry[]>([])
  const [loading, setLoading] = React.useState(true)
  const [sortKey, setSortKey] = React.useState<SortKey>("dailyDeals")
  const [sortAsc, setSortAsc] = React.useState(false)

  React.useEffect(() => {
    const load = async () => {
      setLoading(true)
      const res = await getAdminAgentPerformance()
      if (res.success && res.agents) setAgents(res.agents)
      setLoading(false)
    }
    load()
  }, [getAdminAgentPerformance])

  const sorted = React.useMemo(() => {
    const copy = [...agents]
    copy.sort((a, b) => {
      const diff = (b[sortKey] as number) - (a[sortKey] as number)
      return sortAsc ? -diff : diff
    })
    return copy
  }, [agents, sortKey, sortAsc])

  const top3 = sorted.slice(0, 3)

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc)
    } else {
      setSortKey(key)
      setSortAsc(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#F37020]/30 border-t-[#F37020] rounded-full animate-spin" />
      </div>
    )
  }

  const SortHeader = ({ label, field }: { label: string; field: SortKey }) => (
    <th
      className="text-right py-3 px-3 text-[11px] font-medium uppercase tracking-wider text-white/30 font-admin cursor-pointer select-none hover:text-white/50 transition-colors"
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${sortKey === field ? "text-[#F37020]" : ""}`} />
      </span>
    </th>
  )

  return (
    <div className="space-y-6">
      {/* Top 3 Performers */}
      {top3.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {top3.map((agent, i) => (
            <motion.div
              key={agent._id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="bg-[#111117] rounded-xl p-5 border border-[#2A2A45] relative overflow-hidden"
              style={{ borderColor: `${PODIUM_COLORS[i]}30` }}
            >
              <div className="absolute top-3 right-3">
                <Trophy className="w-5 h-5" style={{ color: PODIUM_COLORS[i] }} />
              </div>
              <div className="flex items-center gap-3 mb-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-lg font-bold font-mono-admin"
                  style={{ background: `${PODIUM_COLORS[i]}15`, color: PODIUM_COLORS[i] }}
                >
                  #{i + 1}
                </div>
                <div>
                  <p className="text-sm font-semibold text-white font-admin truncate">{agent.name}</p>
                  <p className="text-[11px] text-white/30 font-admin">{agent.agencyName}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-lg font-semibold text-white font-mono-admin">{agent.dailyDeals}</p>
                  <p className="text-[10px] text-white/30 uppercase font-admin">Daily</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-white font-mono-admin">{agent.monthlyDeals}</p>
                  <p className="text-[10px] text-white/30 uppercase font-admin">Monthly</p>
                </div>
                <div>
                  <p className="text-lg font-semibold text-white font-mono-admin">{agent.totalDeals}</p>
                  <p className="text-[10px] text-white/30 uppercase font-admin">Total</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Data Table */}
      <AdminCard delay={0.15}>
        <h3 className="text-sm font-semibold text-white/70 font-admin mb-4">
          All Agents ({agents.length})
        </h3>

        {agents.length === 0 ? (
          <p className="text-center py-8 text-white/30 text-sm font-admin">No agents found</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#2A2A45]">
                  <th className="text-left py-3 px-3 text-[11px] font-medium uppercase tracking-wider text-white/30 font-admin">Rank</th>
                  <th className="text-left py-3 px-3 text-[11px] font-medium uppercase tracking-wider text-white/30 font-admin">Agent</th>
                  <th className="text-left py-3 px-3 text-[11px] font-medium uppercase tracking-wider text-white/30 font-admin">Agency</th>
                  <SortHeader label="Daily" field="dailyDeals" />
                  <SortHeader label="Monthly" field="monthlyDeals" />
                  <SortHeader label="Total" field="totalDeals" />
                  <SortHeader label="Points" field="points" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((agent, i) => (
                  <motion.tr
                    key={agent._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.02 }}
                    className="border-b border-[#2A2A45]/50 hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="py-3 px-3 font-mono-admin text-white/40">{i + 1}</td>
                    <td className="py-3 px-3">
                      <span className="text-white/80 font-admin">{agent.name}</span>
                      <span className="block text-[11px] text-white/25">{agent.email}</span>
                    </td>
                    <td className="py-3 px-3 text-white/40 font-admin text-xs">{agent.agencyName}</td>
                    <td className="py-3 px-3 text-right font-mono-admin text-white/70">{agent.dailyDeals}</td>
                    <td className="py-3 px-3 text-right font-mono-admin text-white/70">{agent.monthlyDeals}</td>
                    <td className="py-3 px-3 text-right font-mono-admin text-white/70">{agent.totalDeals}</td>
                    <td className="py-3 px-3 text-right font-mono-admin text-[#F37020]">{agent.points}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>
    </div>
  )
}
